import { IMovementRS, IMovementElementItem, TStorageItemRS, TModuleOptions, TPayment } from '../models/storageModel'
import { getPaymentMethodLabel } from './index'

export const roundValue = (val: number): number => {
  return Math.round((val + Number.EPSILON) * 100) / 100
}

export const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  PURCHASE_REQUEST: 'Solicitud de Compra',
  PURCHASE_ORDER: 'Orden de Compra',
  PURCHASE_RECEIPT: 'Recepción de Compra',
  SALE_INVOICE: 'Factura de Venta',
  TRANSFER: 'Transferencia entre Almacenes',
  ADJUSTMENT: 'Ajuste de Inventario',
  RETURN_IN: 'Devolución de Cliente',
  RETURN_OUT: 'Devolución a Proveedor',
  INITIAL_INVENTORY: 'Inventario Inicial',
  PARTNER_CONTRIBUTION: 'Aporte de Socio',
  PRODUCTION_ENTRY: 'Entrada de Producción',
  PRODUCTION_EXIT: 'Salida de Producción',
  QUOTATION: 'Cotización',
  WASTE: 'Merma / Desperdicio',
  CONVERSION: 'Conversión'
}

export const ENTRY_DOCUMENT_TYPES = [
  'PURCHASE_RECEIPT',
  'RETURN_IN',
  'INITIAL_INVENTORY',
  'PARTNER_CONTRIBUTION',
  'PRODUCTION_ENTRY',
  'CONVERSION'
]

export const EXIT_DOCUMENT_TYPES = [
  'SALE_INVOICE',
  'RETURN_OUT',
  'WASTE',
  'PRODUCTION_EXIT'
]

export const PREPARATORY_DOCUMENT_TYPES = [
  'PURCHASE_ORDER',
  'PURCHASE_REQUEST',
  'QUOTATION'
]

export function getMovementTypeLabel(type: string): string {
  return MOVEMENT_TYPE_LABELS[type] || type || 'Desconocido'
}

export function isEntryMovement(typeOrMov: string | IMovementRS | any): boolean {
  if (!typeOrMov) return false
  if (typeof typeOrMov === 'object') {
    const docType = typeOrMov.document_type
    if (PREPARATORY_DOCUMENT_TYPES.includes(docType) || docType === 'TRANSFER') return false
    if (docType && ENTRY_DOCUMENT_TYPES.includes(docType)) return true
    if (docType && EXIT_DOCUMENT_TYPES.includes(docType)) return false
    if (typeOrMov.storehouse_to_id && !typeOrMov.storehouse_from_id) return true
    if (typeOrMov.storehouse_from_id && !typeOrMov.storehouse_to_id) return false
    return false
  }
  if (PREPARATORY_DOCUMENT_TYPES.includes(typeOrMov) || typeOrMov === 'TRANSFER') return false
  return ENTRY_DOCUMENT_TYPES.includes(typeOrMov)
}

export function isExitMovement(typeOrMov: string | IMovementRS | any): boolean {
  if (!typeOrMov) return false
  if (typeof typeOrMov === 'object') {
    const docType = typeOrMov.document_type
    if (PREPARATORY_DOCUMENT_TYPES.includes(docType) || docType === 'TRANSFER') return false
    if (docType && EXIT_DOCUMENT_TYPES.includes(docType)) return true
    if (docType && ENTRY_DOCUMENT_TYPES.includes(docType)) return false
    if (typeOrMov.storehouse_from_id && !typeOrMov.storehouse_to_id) return true
    if (typeOrMov.storehouse_to_id && !typeOrMov.storehouse_from_id) return false
    return false
  }
  if (PREPARATORY_DOCUMENT_TYPES.includes(typeOrMov) || typeOrMov === 'TRANSFER') return false
  return EXIT_DOCUMENT_TYPES.includes(typeOrMov)
}

export interface InventoryFulfillmentInfo {
  label: string;
  shortLabel: string;
  tooltip: string;
  color: string;
  iconType: 'receipt' | 'delivery' | 'transfer';
  status: 'PENDING' | 'PARTIALLY_RECEIVED' | 'RECEIVED';
}

export function getInventoryFulfillmentInfo(documentType: string, receptionStatus?: string | null): InventoryFulfillmentInfo {
  if (['PURCHASE_ORDER', 'PURCHASE_REQUEST', 'QUOTATION'].includes(documentType)) {
    return {
      label: documentType === 'QUOTATION' ? 'Cotización (Comercial)' : 'Orden Emitida (Comercial)',
      shortLabel: documentType === 'QUOTATION' ? 'Cotización' : 'Orden Emitida',
      tooltip: 'Documento preparatorio sin afectación física de inventario',
      color: 'default',
      iconType: 'receipt',
      status: 'PENDING'
    };
  }

  const isExit = isExitMovement(documentType);
  const isTransfer = documentType === 'TRANSFER';
  const status = (receptionStatus || 'PENDING') as 'PENDING' | 'PARTIALLY_RECEIVED' | 'RECEIVED';

  if (isExit) {
    switch (status) {
      case 'RECEIVED':
        return {
          label: 'Despacho: Entregado Total',
          shortLabel: 'Despacho: Entregado',
          tooltip: 'Toda la mercancía ha sido despachada y entregada',
          color: 'green',
          iconType: 'delivery',
          status
        };
      case 'PARTIALLY_RECEIVED':
        return {
          label: 'Despacho: Entregado Parcial',
          shortLabel: 'Despacho: Parcial',
          tooltip: 'Despacho parcial de mercancía en curso',
          color: 'cyan',
          iconType: 'delivery',
          status
        };
      default:
        return {
          label: 'Despacho: Pendiente Salida',
          shortLabel: 'Despacho: Pendiente',
          tooltip: 'Mercancía pendiente por despachar de bodega',
          color: 'volcano',
          iconType: 'delivery',
          status
        };
    }
  }

  if (isTransfer) {
    switch (status) {
      case 'RECEIVED':
        return {
          label: 'Traslado: Completado',
          shortLabel: 'Traslado: Completado',
          tooltip: 'Mercancía recibida en bodega destino',
          color: 'green',
          iconType: 'transfer',
          status
        };
      case 'PARTIALLY_RECEIVED':
        return {
          label: 'Traslado: En Tránsito Parcial',
          shortLabel: 'Traslado: En Tránsito',
          tooltip: 'Traslado en curso o recepción parcial',
          color: 'cyan',
          iconType: 'transfer',
          status
        };
      default:
        return {
          label: 'Traslado: Pendiente Salida',
          shortLabel: 'Traslado: Pendiente',
          tooltip: 'Traslado pendiente de ejecución física',
          color: 'volcano',
          iconType: 'transfer',
          status
        };
    }
  }

  // Compras y Entradas (Por defecto)
  switch (status) {
    case 'RECEIVED':
      return {
        label: 'Bodega: Recibido Total',
        shortLabel: 'Bodega: Recibido',
        tooltip: 'Mercancía ingresada totalmente a bodega',
        color: 'green',
        iconType: 'receipt',
        status
      };
    case 'PARTIALLY_RECEIVED':
      return {
        label: 'Bodega: Recibido Parcial',
        shortLabel: 'Bodega: Parcial',
        tooltip: 'Ingreso parcial a bodega en curso',
        color: 'cyan',
        iconType: 'receipt',
        status
      };
    default:
      return {
        label: 'Bodega: Pendiente Entrada',
        shortLabel: 'Bodega: Pendiente',
        tooltip: 'Mercancía pendiente por ingresar a bodega',
        color: 'volcano',
        iconType: 'receipt',
        status
      };
  }
}

// ----------------------------------------------------
// TIPOS E INFORMES DE MOVIMIENTOS
// ----------------------------------------------------

export type TMovementSummary = {
  totalMovements: number
  totalEntriesAmount: number
  totalExitsAmount: number
  netBalanceAmount: number
  totalItemsMoved: number
  totalTaxes: number
  totalDiscounts: number
}

export type TGroupedData = {
  key: string
  label: string
  count: number
  totalAmount: number
  totalTaxes: number
  totalDiscounts: number
  percentage: number
}

export type TStorehouseMovementGroup = {
  key: string
  storehouseName: string
  entriesCount: number
  entriesAmount: number
  exitsCount: number
  exitsAmount: number
  netBalance: number
}

export type TProductMovedRanking = {
  key: string
  itemCode: string
  itemName: string
  entryQuantity: number
  exitQuantity: number
  totalQuantity: number
  totalAmount: number
  averagePrice: number
  transactionCount: number
}

export type TTransferGroup = {
  key: string
  documentNumber: string
  createdAt: string
  fromStorehouse: string
  toStorehouse: string
  itemsCount: number
  totalAmount: number
  userName: string
}

export function calculateMovementSummary(movements: IMovementRS[]): TMovementSummary {
  let totalEntriesAmount = 0
  let totalExitsAmount = 0
  let totalTaxes = 0
  let totalDiscounts = 0
  let totalItemsMoved = 0

  movements.forEach(m => {
    const isEntry = isEntryMovement(m)
    const isExit = isExitMovement(m)
    const isTransfer = m.document_type === 'TRANSFER'
    const amount = m.total_amount_net || m.total_amount_full || m.total_amount || 0

    if (isEntry) {
      totalEntriesAmount += amount
    } else if (isExit) {
      totalExitsAmount += amount
    } else if (isTransfer) {
      // Transfer no altera balance financiero neto global
    }

    if (isEntry || isExit || isTransfer) {
      if (m.item_list && Array.isArray(m.item_list)) {
        const movedCount = m.item_list.filter((itemLine: any) => {
          const qty = (itemLine.quantity_received !== undefined && itemLine.quantity_received !== null)
            ? Number(itemLine.quantity_received)
            : Number(itemLine.quantity)
          return !Number.isNaN(qty) && qty > 0
        }).length
        totalItemsMoved += movedCount
      } else {
        totalItemsMoved += m.items || 0
      }
    }

    totalTaxes += m.total_tax_amount || 0
    totalDiscounts += m.total_discount_amount || 0
  })

  return {
    totalMovements: movements.length,
    totalEntriesAmount: roundValue(totalEntriesAmount),
    totalExitsAmount: roundValue(totalExitsAmount),
    netBalanceAmount: roundValue(totalEntriesAmount - totalExitsAmount),
    totalItemsMoved,
    totalTaxes: roundValue(totalTaxes),
    totalDiscounts: roundValue(totalDiscounts)
  }
}

export function groupMovementsByType(movements: IMovementRS[]): TGroupedData[] {
  const map = new Map<string, TGroupedData>()
  const grandTotal = movements.reduce((acc, m) => acc + (m.total_amount_net || m.total_amount_full || m.total_amount || 0), 0)

  movements.forEach(m => {
    const key = m.document_type || 'OTRO'
    const label = getMovementTypeLabel(key)
    const amount = m.total_amount_net || m.total_amount_full || m.total_amount || 0
    const existing = map.get(key)

    if (existing) {
      existing.count++
      existing.totalAmount += amount
      existing.totalTaxes += m.total_tax_amount || 0
      existing.totalDiscounts += m.total_discount_amount || 0
    } else {
      map.set(key, {
        key,
        label,
        count: 1,
        totalAmount: amount,
        totalTaxes: m.total_tax_amount || 0,
        totalDiscounts: m.total_discount_amount || 0,
        percentage: 0
      })
    }
  })

  return Array.from(map.values())
    .map(item => ({
      ...item,
      totalAmount: roundValue(item.totalAmount),
      totalTaxes: roundValue(item.totalTaxes),
      totalDiscounts: roundValue(item.totalDiscounts),
      percentage: grandTotal > 0 ? roundValue((item.totalAmount / grandTotal) * 100) : 0
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
}

export function groupMovementsByStorehouse(
  movements: IMovementRS[],
  storehouseMap: Map<string | number, string>
): TStorehouseMovementGroup[] {
  const map = new Map<string, TStorehouseMovementGroup>()

  const getOrCreate = (id: string | number | undefined, record?: any, isFrom: boolean = true) => {
    const key = String(id || 'unknown')
    const name = (id && storehouseMap.get(id))
      || (id && storehouseMap.get(String(id)))
      || (isFrom ? record?.storehouse_from_name : record?.storehouse_to_name)
      || record?.storehouse_name
      || `Almacén #${key}`
    if (!map.has(key)) {
      map.set(key, {
        key,
        storehouseName: name,
        entriesCount: 0,
        entriesAmount: 0,
        exitsCount: 0,
        exitsAmount: 0,
        netBalance: 0
      })
    }
    return map.get(key)!
  }

  movements.forEach(m => {
    const amount = m.total_amount_net || m.total_amount_full || m.total_amount || 0
    const isEntryMov = isEntryMovement(m.document_type)
    const isExitMov = isExitMovement(m.document_type)
    const isTransferMov = m.document_type === 'TRANSFER'

    // Almacén Origen (Salidas o Origen de Transferencia)
    if (m.storehouse_from_id && (isExitMov || isTransferMov || !m.storehouse_to_id)) {
      const fromGroup = getOrCreate(m.storehouse_from_id, m, true)
      if (isExitMov || isTransferMov) {
        fromGroup.exitsCount++
        fromGroup.exitsAmount += amount
      } else {
        fromGroup.entriesCount++
        fromGroup.entriesAmount += amount
      }
    }

    // Almacén Destino (Entradas o Destino de Transferencia)
    if (m.storehouse_to_id && (isEntryMov || isTransferMov)) {
      const toGroup = getOrCreate(m.storehouse_to_id, m, false)
      toGroup.entriesCount++
      toGroup.entriesAmount += amount
    }
  })

  return Array.from(map.values()).map(g => ({
    ...g,
    entriesAmount: roundValue(g.entriesAmount),
    exitsAmount: roundValue(g.exitsAmount),
    netBalance: roundValue(g.entriesAmount - g.exitsAmount)
  })).sort((a, b) => (b.entriesAmount + b.exitsAmount) - (a.entriesAmount + a.exitsAmount))
}

export function groupMovementsByContact(movements: IMovementRS[]): TGroupedData[] {
  const map = new Map<string, TGroupedData>()
  const grandTotal = movements.reduce((acc, m) => acc + (m.total_amount_net || m.total_amount_full || m.total_amount || 0), 0)

  movements.forEach(m => {
    const key = m.contact_name || m.contact_document || 'Sin Contacto'
    const amount = m.total_amount_net || m.total_amount_full || m.total_amount || 0
    const existing = map.get(key)

    if (existing) {
      existing.count++
      existing.totalAmount += amount
      existing.totalTaxes += m.total_tax_amount || 0
      existing.totalDiscounts += m.total_discount_amount || 0
    } else {
      map.set(key, {
        key,
        label: key,
        count: 1,
        totalAmount: amount,
        totalTaxes: m.total_tax_amount || 0,
        totalDiscounts: m.total_discount_amount || 0,
        percentage: 0
      })
    }
  })

  return Array.from(map.values())
    .map(item => ({
      ...item,
      totalAmount: roundValue(item.totalAmount),
      totalTaxes: roundValue(item.totalTaxes),
      totalDiscounts: roundValue(item.totalDiscounts),
      percentage: grandTotal > 0 ? roundValue((item.totalAmount / grandTotal) * 100) : 0
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
}

export function extractProductKeysFromMovementItem(item: IMovementElementItem): string[] {
  const keys: string[] = []
  if (item.custom_id) keys.push(String(item.custom_id).trim())
  if (item.item_code) keys.push(String(item.item_code).trim())
  if ((item as any).element_id) keys.push(String((item as any).element_id).trim())
  if (item.id && typeof item.id === 'number' && item.id > 0) keys.push(String(item.id).trim())
  if (item.item_name) keys.push(String(item.item_name).trim().toLowerCase())
  if (item.name) keys.push(String(item.name).trim().toLowerCase())
  return Array.from(new Set(keys.filter(Boolean)))
}

export function extractProductKeysFromStorageItem(item: TStorageItemRS): string[] {
  const keys: string[] = []
  if (item.custom_id) keys.push(String(item.custom_id).trim())
  if (item.sku) keys.push(String(item.sku).trim())
  if (item.id && typeof item.id === 'number' && item.id > 0) keys.push(String(item.id).trim())
  if (item.uid) keys.push(String(item.uid).trim())
  if (item.name) keys.push(String(item.name).trim().toLowerCase())
  return Array.from(new Set(keys.filter(Boolean)))
}

export function getTopMovedProducts(movements: IMovementRS[], limit: number = 50): TProductMovedRanking[] {
  const map = new Map<string, TProductMovedRanking>()

  movements.forEach(m => {
    const isEntry = isEntryMovement(m)
    const isExit = isExitMovement(m)
    if (!isEntry && !isExit) return

    if (m.item_list && Array.isArray(m.item_list)) {
      m.item_list.forEach((item: IMovementElementItem) => {
        const itemKeys = extractProductKeysFromMovementItem(item)
        const primaryKey = itemKeys[0] || item.item_code || item.custom_id || item.item_name || item.name
        if (!primaryKey) return

        const physicalQty = (item.quantity_received !== undefined && item.quantity_received !== null)
          ? Number(item.quantity_received)
          : Number(item.quantity)
        const qty = Number.isNaN(physicalQty) ? 0 : physicalQty
        if (qty <= 0) return

        const existing = map.get(primaryKey)
        const unitPrice = Number(item.unit_price) || 0
        const calculatedAmount = qty * unitPrice
        const amount = item.total_amount_net || item.total_amount_full || item.total_amount || calculatedAmount || 0

        const entryQty = isEntry ? qty : 0
        const exitQty = isExit ? qty : 0

        if (existing) {
          existing.entryQuantity += entryQty
          existing.exitQuantity += exitQty
          existing.totalQuantity += qty
          existing.totalAmount += amount
          existing.transactionCount++
        } else {
          map.set(primaryKey, {
            key: primaryKey,
            itemCode: item.item_code || item.custom_id || item.key || primaryKey,
            itemName: item.item_name || item.name || 'Producto sin nombre',
            entryQuantity: entryQty,
            exitQuantity: exitQty,
            totalQuantity: qty,
            totalAmount: amount,
            averagePrice: 0,
            transactionCount: 1
          })
        }
      })
    }
  })

  return Array.from(map.values())
    .map(p => ({
      ...p,
      totalAmount: roundValue(p.totalAmount),
      averagePrice: p.totalQuantity > 0 ? roundValue(p.totalAmount / p.totalQuantity) : 0
    }))
    .sort((a, b) => b.totalQuantity - a.totalQuantity)
    .slice(0, limit)
}

export function getMovementPaymentLabel(m: IMovementRS, paymentOptions?: TModuleOptions[]): string {
  if (m.payments && m.payments.length > 0) {
    const methods = m.payments.map((p: TPayment) => {
      const found = paymentOptions?.find(opt => opt.key === p.payment_method || opt.key === p.payment_method_code)
      return found?.string_value || getPaymentMethodLabel(p.payment_method || p.payment_method_code, paymentOptions) || 'Contado'
    })
    return Array.from(new Set(methods)).join(', ')
  }

  if (m.payment_term === 'CREDIT') return 'Crédito'
  if (m.payment_term === 'CASH') return 'Contado'
  return 'Contado'
}

export function groupMovementsByPaymentMethod(
  movements: IMovementRS[],
  paymentOptions?: TModuleOptions[]
): TGroupedData[] {
  const map = new Map<string, TGroupedData>()

  const movementsWithPayment = movements.filter(m => (m.payments && m.payments.length > 0) || m.payment_term)
  const grandTotal = movementsWithPayment.reduce((acc, m) => acc + (m.total_amount_net || m.total_amount_full || m.total_amount || 0), 0)

  movementsWithPayment.forEach(m => {
    const key = getMovementPaymentLabel(m, paymentOptions)
    const amount = m.total_amount_net || m.total_amount_full || m.total_amount || 0
    const existing = map.get(key)

    if (existing) {
      existing.count++
      existing.totalAmount += amount
      existing.totalTaxes += m.total_tax_amount || 0
      existing.totalDiscounts += m.total_discount_amount || 0
    } else {
      map.set(key, {
        key,
        label: key,
        count: 1,
        totalAmount: amount,
        totalTaxes: m.total_tax_amount || 0,
        totalDiscounts: m.total_discount_amount || 0,
        percentage: 0
      })
    }
  })

  return Array.from(map.values())
    .map(item => ({
      ...item,
      totalAmount: roundValue(item.totalAmount),
      totalTaxes: roundValue(item.totalTaxes),
      totalDiscounts: roundValue(item.totalDiscounts),
      percentage: grandTotal > 0 ? roundValue((item.totalAmount / grandTotal) * 100) : 0
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
}

export function groupMovementsByOperator(movements: IMovementRS[]): TGroupedData[] {
  const map = new Map<string, TGroupedData>()
  const grandTotal = movements.reduce((acc, m) => acc + (m.total_amount_net || m.total_amount_full || m.total_amount || 0), 0)

  movements.forEach(m => {
    const key = m.created_by_user_name || m.created_by_user_document || 'Sistema'
    const amount = m.total_amount_net || m.total_amount_full || m.total_amount || 0
    const existing = map.get(key)

    if (existing) {
      existing.count++
      existing.totalAmount += amount
      existing.totalTaxes += m.total_tax_amount || 0
      existing.totalDiscounts += m.total_discount_amount || 0
    } else {
      map.set(key, {
        key,
        label: key,
        count: 1,
        totalAmount: amount,
        totalTaxes: m.total_tax_amount || 0,
        totalDiscounts: m.total_discount_amount || 0,
        percentage: 0
      })
    }
  })

  return Array.from(map.values())
    .map(item => ({
      ...item,
      totalAmount: roundValue(item.totalAmount),
      totalTaxes: roundValue(item.totalTaxes),
      totalDiscounts: roundValue(item.totalDiscounts),
      percentage: grandTotal > 0 ? roundValue((item.totalAmount / grandTotal) * 100) : 0
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
}

export function getTransferSummary(
  movements: IMovementRS[],
  storehouseMap: Map<string | number, string>
): TTransferGroup[] {
  const transfers = movements.filter(m => m.document_type === 'TRANSFER')

  return transfers.map((m, i) => {
    const fromName = (m.storehouse_from_id && storehouseMap.get(m.storehouse_from_id)) || `Almacén #${m.storehouse_from_id}`
    const toName = (m.storehouse_to_id && storehouseMap.get(m.storehouse_to_id)) || `Almacén #${m.storehouse_to_id}`

    return {
      key: m.uid || String(i),
      documentNumber: m.document_number || `TRF-${i + 1}`,
      createdAt: m.created_at || '',
      fromStorehouse: fromName,
      toStorehouse: toName,
      itemsCount: m.items || m.item_list?.length || 0,
      totalAmount: roundValue(m.total_amount_net || m.total_amount_full || m.total_amount || 0),
      userName: m.created_by_user_name || 'Sistema'
    }
  })
}

// ----------------------------------------------------
// TIPOS E INFORMES DE INVENTARIO
// ----------------------------------------------------

export type TInventorySummary = {
  totalElements: number
  totalProducts: number
  totalServices: number
  totalInventoryQuantity: number
  totalCostValuation: number
  totalSellValuation: number
}

export type TCategoryStockGroup = {
  key: string
  categoryName: string
  itemCount: number
  totalQuantity: number
  totalCostValuation: number
  percentage: number
}

export type TInventoryValuationRow = {
  key: string
  code: string
  name: string
  category: string
  totalQuantity: number
  purchasePrice: number
  sellPrice: number
  totalCost: number
  totalSell: number
  estimatedMargin: number
}

export type TProductRotationRow = {
  key: string
  code: string
  name: string
  currentStock: number
  entryQuantity: number
  exitQuantity: number
  rotationIndex: number
  estimatedDays: number
}

export type TStockAlertRow = {
  key: string
  code: string
  name: string
  category: string
  registerType: string
  quantity: number
  alertType: 'OUT_OF_STOCK' | 'NEGATIVE' | 'BELOW_MIN' | 'STAGNANT'
  alertLabel: string
}

export function calculateInventorySummary(
  items: TStorageItemRS[],
  storehouses: any[] = []
): TInventorySummary {
  let totalProducts = 0
  let totalServices = 0
  let totalInventoryQuantity = 0
  let totalCostValuation = 0
  let totalSellValuation = 0

  items.forEach(item => {
    if (item.register_type === 'SERVICE') {
      totalServices++
      return
    }

    totalProducts++
    const qty = (item.inventory && Array.isArray(item.inventory))
      ? item.inventory.reduce((sum, inv) => sum + (inv.quantity || 0), 0)
      : (item.quantity || 0)

    const purchasePrice = item.purchase_price || 0
    const sellPrice = item.sell_price || 0

    totalInventoryQuantity += qty
    totalCostValuation += qty * purchasePrice
    totalSellValuation += qty * sellPrice
  })

  return {
    totalElements: items.length,
    totalProducts,
    totalServices,
    totalInventoryQuantity,
    totalCostValuation: roundValue(totalCostValuation),
    totalSellValuation: roundValue(totalSellValuation)
  }
}

export function groupItemsByCategory(
  items: TStorageItemRS[],
  categoryOptions: TModuleOptions[] = []
): TCategoryStockGroup[] {
  const map = new Map<string, TCategoryStockGroup>()

  const getCategoryName = (catKey: string) => {
    const found = categoryOptions.find(opt => opt.key === catKey || opt.string_value === catKey)
    return found?.string_value || catKey || 'Sin Categoría'
  }

  let grandValuation = 0

  items.forEach(item => {
    if (item.register_type === 'SERVICE') return

    const catKey = item.category || 'VRS'
    const catName = getCategoryName(catKey)
    const qty = (item.inventory && Array.isArray(item.inventory))
      ? item.inventory.reduce((sum, inv) => sum + (inv.quantity || 0), 0)
      : (item.quantity || 0)
    const cost = qty * (item.purchase_price || 0)
    grandValuation += cost

    const existing = map.get(catKey)
    if (existing) {
      existing.itemCount++
      existing.totalQuantity += qty
      existing.totalCostValuation += cost
    } else {
      map.set(catKey, {
        key: catKey,
        categoryName: catName,
        itemCount: 1,
        totalQuantity: qty,
        totalCostValuation: cost,
        percentage: 0
      })
    }
  })

  return Array.from(map.values())
    .map(g => ({
      ...g,
      totalCostValuation: roundValue(g.totalCostValuation),
      percentage: grandValuation > 0 ? roundValue((g.totalCostValuation / grandValuation) * 100) : 0
    }))
    .sort((a, b) => b.totalCostValuation - a.totalCostValuation)
}

export function getInventoryValuation(
  items: TStorageItemRS[],
  categoryOptions: TModuleOptions[] = []
): TInventoryValuationRow[] {
  const getCategoryName = (catKey: string) => {
    const found = categoryOptions.find(opt => opt.key === catKey)
    return found?.string_value || catKey || 'Sin Categoría'
  }

  return items
    .filter(item => item.register_type !== 'SERVICE')
    .map(item => {
      const qty = (item.inventory && Array.isArray(item.inventory))
        ? item.inventory.reduce((sum, inv) => sum + (inv.quantity || 0), 0)
        : (item.quantity || 0)

      const purchasePrice = item.purchase_price || 0
      const sellPrice = item.sell_price || 0
      const totalCost = roundValue(qty * purchasePrice)
      const totalSell = roundValue(qty * sellPrice)
      const estimatedMargin = totalSell > 0 ? roundValue(((totalSell - totalCost) / totalSell) * 100) : 0

      return {
        key: item.uid || String(item.id),
        code: item.custom_id || item.sku || String(item.id),
        name: item.name,
        category: getCategoryName(item.category),
        totalQuantity: qty,
        purchasePrice: roundValue(purchasePrice),
        sellPrice: roundValue(sellPrice),
        totalCost,
        totalSell,
        estimatedMargin
      }
    })
    .sort((a, b) => b.totalCost - a.totalCost)
}

export function getProductRotation(
  items: TStorageItemRS[],
  movements: IMovementRS[]
): TProductRotationRow[] {
  // Mapear cada alias (código, sku, id, nombre) al UID único del producto
  const aliasToUidMap = new Map<string, string>()
  items.forEach(item => {
    const primaryUid = item.uid || String(item.id)
    const keys = extractProductKeysFromStorageItem(item)
    keys.forEach(k => aliasToUidMap.set(k, primaryUid))
  })

  // Indexar cantidades de movimientos sin duplicar por alias
  const movedMap = new Map<string, { entry: number; exit: number }>()

  movements.forEach(m => {
    const isEntry = isEntryMovement(m)
    const isExit = isExitMovement(m)
    if (!isEntry && !isExit) return

    if (m.item_list && Array.isArray(m.item_list)) {
      m.item_list.forEach((itemLine: IMovementElementItem) => {
        const physicalQty = (itemLine.quantity_received !== undefined && itemLine.quantity_received !== null)
          ? Number(itemLine.quantity_received)
          : Number(itemLine.quantity)
        const qty = Number.isNaN(physicalQty) ? 0 : physicalQty
        if (qty <= 0) return

        const lineKeys = extractProductKeysFromMovementItem(itemLine)

        // Encontrar la clave canónica única del producto
        let canonicalUid: string | null = null
        for (const k of lineKeys) {
          if (aliasToUidMap.has(k)) {
            canonicalUid = aliasToUidMap.get(k)!
            break
          }
        }
        if (!canonicalUid && lineKeys.length > 0) {
          canonicalUid = lineKeys[0]
        }

        if (canonicalUid) {
          const existing = movedMap.get(canonicalUid) || { entry: 0, exit: 0 }
          if (isEntry) {
            existing.entry += qty
          } else if (isExit) {
            existing.exit += qty
          }
          movedMap.set(canonicalUid, existing)
        }
      })
    }
  })

  return items
    .filter(item => item.register_type !== 'SERVICE')
    .map(item => {
      const primaryUid = item.uid || String(item.id)
      const itemKeys = extractProductKeysFromStorageItem(item)

      let movementData = movedMap.get(primaryUid)
      if (!movementData) {
        for (const k of itemKeys) {
          if (movedMap.has(k)) {
            movementData = movedMap.get(k)
            break
          }
        }
      }
      movementData ??= { entry: 0, exit: 0 };

      const currentStock = (item.inventory && Array.isArray(item.inventory))
        ? item.inventory.reduce((sum, inv) => sum + (inv.quantity || 0), 0)
        : (item.quantity || 0)

      const entryQty = movementData.entry
      const exitQty = movementData.exit

      const avgStock = currentStock > 0 ? currentStock : 1
      const rotationIndex = roundValue(exitQty / avgStock)
      const estimatedDays = rotationIndex > 0 ? roundValue(30 / rotationIndex) : 999

      return {
        key: primaryUid,
        code: item.custom_id || item.sku || String(item.id),
        name: item.name,
        currentStock,
        entryQuantity: entryQty,
        exitQuantity: exitQty,
        rotationIndex,
        estimatedDays
      }
    })
    .sort((a, b) => b.exitQuantity - a.exitQuantity)
}

export function getStockAlerts(
  items: TStorageItemRS[],
  movements: IMovementRS[],
  categoryOptions: TModuleOptions[] = []
): TStockAlertRow[] {
  const getCategoryName = (catKey: string) => {
    const found = categoryOptions.find(opt => opt.key === catKey)
    return found?.string_value || catKey || 'Sin Categoría'
  }

  const movedKeys = new Set<string>()
  movements.forEach(m => {
    const isEntry = isEntryMovement(m)
    const isExit = isExitMovement(m)
    if (!isEntry && !isExit) return

    if (m.item_list && Array.isArray(m.item_list)) {
      m.item_list.forEach((item: IMovementElementItem) => {
        const physicalQty = (item.quantity_received !== undefined && item.quantity_received !== null)
          ? Number(item.quantity_received)
          : Number(item.quantity)
        if (physicalQty > 0) {
          const keys = extractProductKeysFromMovementItem(item)
          keys.forEach(k => movedKeys.add(k))
        }
      })
    }
  })

  const alerts: TStockAlertRow[] = []

  items.forEach(item => {
    if (item.register_type === 'SERVICE') return

    const qty = (item.inventory && Array.isArray(item.inventory))
      ? item.inventory.reduce((sum, inv) => sum + (inv.quantity || 0), 0)
      : (item.quantity || 0)

    const itemKeys = extractProductKeysFromStorageItem(item)
    const hasMoved = itemKeys.some(k => movedKeys.has(k))
    const minStock = item.min_quantity ?? (item.attributes as any)?.min_stock

    let alertType: TStockAlertRow['alertType'] | null = null
    let alertLabel = ''

    if (qty < 0) {
      alertType = 'NEGATIVE'
      alertLabel = 'Existencia Negativa'
    } else if (qty === 0) {
      alertType = 'OUT_OF_STOCK'
      alertLabel = 'Sin Stock / Agotado'
    } else if (minStock !== undefined && minStock !== null && qty <= Number(minStock)) {
      alertType = 'BELOW_MIN'
      alertLabel = `Bajo del Mínimo (Mín: ${minStock})`
    } else if (movements.length > 0 && !hasMoved && qty > 0) {
      alertType = 'STAGNANT'
      alertLabel = 'Sin Movimiento en el Período'
    }

    if (alertType) {
      alerts.push({
        key: item.uid || String(item.id),
        code: item.custom_id || item.sku || String(item.id),
        name: item.name,
        category: getCategoryName(item.category),
        registerType: item.register_type,
        quantity: qty,
        alertType,
        alertLabel
      })
    }
  })

  return alerts.sort((a, b) => a.quantity - b.quantity)
}
